import React, { useState, useEffect } from "react";
import Select from "react-select";

const CreateLeadAbroadForm = ({
  mode,
  formData,
  setFormData,
  handleChange,
  sectionsRef,
}) => {
  // Select options for different fields
  const studyAbroadCountries = [
    { value: "USA", label: "USA" },
    { value: "UK", label: "UK" },
    { value: "Canada", label: "Canada" },
    { value: "Australia", label: "Australia" },
    { value: "Germany", label: "Germany" },
    { value: "France", label: "France" },
    { value: "New Zealand", label: "New Zealand" },
    { value: "Singapore", label: "Singapore" },
    { value: "Other", label: "Other" },
  ];

  const monthOptions = [
    { value: "January", label: "January" },
    { value: "February", label: "February" },
    { value: "March", label: "March" },
    { value: "April", label: "April" },
    { value: "May", label: "May" },
    { value: "June", label: "June" },
    { value: "July", label: "July" },
    { value: "August", label: "August" },
    { value: "September", label: "September" },
    { value: "October", label: "October" },
    { value: "November", label: "November" },
    { value: "December", label: "December" },
  ];

  // Get current month and year
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // 0-11
  const currentYear = currentDate.getFullYear();

  const generateMonthOptions = (selectedYear) => {
    const allMonths = [
      { value: "January", label: "January" },
      { value: "February", label: "February" },
      { value: "March", label: "March" },
      { value: "April", label: "April" },
      { value: "May", label: "May" },
      { value: "June", label: "June" },
      { value: "July", label: "July" },
      { value: "August", label: "August" },
      { value: "September", label: "September" },
      { value: "October", label: "October" },
      { value: "November", label: "November" },
      { value: "December", label: "December" },
    ];

    // If selected year is the current year, only show current and future months
    if (selectedYear && parseInt(selectedYear) === currentYear) {
      return allMonths.filter((month, index) => index >= currentMonth);
    }

    // For future years or when no year is selected, show all months
    return allMonths;
  };

  const [availableMonths, setAvailableMonths] = useState(
    generateMonthOptions(formData.intakeYear)
  );

  const yearOptions = Array.from({ length: 11 }, (_, index) => {
    const year = currentYear + index;
    return { value: year.toString(), label: year.toString() };
  });

  // const currentYear = new Date().getFullYear();
  // const yearOptions = Array.from({ length: 11 }, (_, index) => {
  //   const year = currentYear + index;
  //   return { value: year.toString(), label: year.toString() };
  // });

  // Custom styles for React Select components
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#4F46E5" : "#E2E8F0",
      boxShadow: state.isFocused ? "0 0 0 1px #4F46E5" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#4F46E5" : "#CBD5E1",
      },
      borderRadius: "0.375rem",
      padding: "2px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#EEF2FF",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#4F46E5",
      fontWeight: 500,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#4F46E5",
      "&:hover": {
        backgroundColor: "#E0E7FF",
        color: "#4338CA",
      },
    }),
  };

  // Handle country selection changes
  const handleCountryChange = (selectedOptions) => {
    const selectedCountries = selectedOptions.map((option) => option.value);
    setFormData({
      ...formData,
      countries_for_abroad_studies: selectedCountries,
    });
  };

  // Handle intake month selection
  const handleMonthChange = (selectedOption) => {
    const month = selectedOption ? selectedOption.value : "";
    const selectedMonthIndex = monthOptions.findIndex((m) => m.value === month);

    if (formData.intakeYear && parseInt(formData.intakeYear) === currentYear) {
      if (selectedMonthIndex < currentMonth) {
        // Invalid selection for current year
        setFormData((prevState) => ({ ...prevState, intakeMonth: "" }));
        return;
      }
    }

    setFormData((prevState) => ({ ...prevState, intakeMonth: month }));
  };

  // Handle intake year selection
  const handleYearChange = (selectedOption) => {
    const year = selectedOption ? selectedOption.value : "";

    // Reset month if year changes to prevent invalid combinations
    if (year && parseInt(year) === currentYear) {
      // If current year selected and current month is already selected but not valid
      const monthIndex = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ].indexOf(formData.intakeMonth);

      if (monthIndex < currentMonth) {
        const newMonth = monthOptions[currentMonth].value;
        setFormData((prevState) => ({
          ...prevState,
          intakeYear: year,
          intakeMonth: newMonth, // Dynamically update to current month
        }));
      } else {
        setFormData((prevState) => ({ ...prevState, intakeYear: year }));
      }
    } else {
      setFormData((prevState) => ({ ...prevState, intakeYear: year }));
    }

    // Update available months based on selected year
    setAvailableMonths(generateMonthOptions(year));
  };

  useEffect(() => {
    setAvailableMonths(generateMonthOptions(formData.intakeYear));
  }, [formData.intakeYear]);

  useEffect(() => {
    // If we're in edit mode and have an existing intake date that's in the past
    if (mode === "edit" && formData.intakeMonth && formData.intakeYear) {
      const selectedYear = parseInt(formData.intakeYear);
      const monthIndex = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ].indexOf(formData.intakeMonth);

      // If the date is in the past (current year but past month)
    }
  }, []);

  return (
    <section
      ref={sectionsRef.educationCareer}
      id="education-career"
      className="mb-8 bg-white rounded-lg shadow-md overflow-hidden"
    >
      <div className="bg-indigo-50 px-6 py-4 border-b border-indigo-100">
        <h2 className="text-lg font-semibold text-indigo-800 flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M12 14l9-5-9-5-9 5 9 5z" />
            <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
            />
          </svg>
          Education & Career Information
        </h2>
      </div>

      <div className="p-6">
        {/* Current and Previous Education */}
        <div className="mb-6">
          <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
            1. Educational Background
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Education */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Current Education
              </label>
              {mode === "view" ? (
                <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                  {formData.current_education || "N/A"}
                </p>
              ) : (
                <input
                  type="text"
                  name="current_education"
                  value={formData.current_education || ""}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Bachelor's in Computer Science (3rd Year)"
                />
              )}
            </div>

            {/* Previous Education */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Previous Education
              </label>
              {mode === "view" ? (
                <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                  {formData.previous_education || "N/A"}
                </p>
              ) : (
                <input
                  type="text"
                  name="previous_education"
                  value={formData.previous_education || ""}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., High School, 12th Science"
                />
              )}
            </div>
          </div>
        </div>

        {/* Academic Performance Section */}
        <div className="mb-6">
          <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
            2. Academic Performance
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* GPA/Percentage Selection */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Academic Result Format
              </label>
              {mode === "view" ? (
                <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                  {formData.gpa_percentage
                    ? formData.gpa_percentage === "percentage"
                      ? "Percentage"
                      : "GPA"
                    : "N/A"}
                </p>
              ) : (
                <div className="flex space-x-4">
                  <div className="flex items-center">
                    <input
                      id="percentage-option"
                      type="radio"
                      name="gpa_percentage"
                      value="percentage"
                      checked={formData.gpa_percentage === "percentage"}
                      onChange={handleChange}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <label
                      htmlFor="percentage-option"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Percentage
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="gpa-option"
                      type="radio"
                      name="gpa_percentage"
                      value="gpa"
                      checked={formData.gpa_percentage === "gpa"}
                      onChange={handleChange}
                      className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <label
                      htmlFor="gpa-option"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      GPA
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Conditionally Render Percentage or GPA Input */}
            {formData.gpa_percentage === "percentage" && (
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Percentage
                </label>
                {mode === "view" ? (
                  <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                    {formData.percentage ? `${formData.percentage}%` : "N/A"}
                  </p>
                ) : (
                  <div className="relative">
                    <input
                      type="number"
                      name="percentage"
                      value={formData.percentage || ""}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md pl-3 pr-10 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter percentage"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500">%</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {formData.gpa_percentage === "gpa" && (
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  GPA
                </label>
                {mode === "view" ? (
                  <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                    {formData.gpa && formData.out_off
                      ? `${formData.gpa} / ${formData.out_off}`
                      : "N/A"}
                  </p>
                ) : (
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      step="0.01"
                      name="gpa"
                      value={formData.gpa || ""}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter GPA"
                      min="0"
                    />
                    <span className="text-gray-500">/</span>
                    <input
                      type="number"
                      name="out_off"
                      value={formData.out_off || ""}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Scale (e.g. 4.0, 10)"
                      min="0"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Study Abroad Plans */}
        <div className="mb-6">
          <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
            3. Study Abroad Plans
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Intended Degree */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Intended Degree
              </label>
              {mode === "view" ? (
                <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                  {formData.intended_degree || "N/A"}
                </p>
              ) : (
                <select
                  id="intended_degree"
                  name="intended_degree"
                  value={formData.intended_degree || ""}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="">Select Degree</option>
                  <option value="Undergraduate">Undergraduate/Bachelors</option>
                  <option value="Masters">Masters</option>
                  <option value="PhD">PhD</option>
                  <option value="Medical">Medical</option>
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Course Shortlisted
              </label>
              {mode === "view" ? (
                <p className="bg-gray-50 p-2 rounded-md border border-gray-200">
                  {formData.intended_courses || "N/A"}
                </p>
              ) : (
                <input
                  type="text"
                  name="intended_courses"
                  value={formData.intended_courses || ""}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Computer Science, Business Analytics, etc."
                />
              )}
            </div>

            {/* Countries Selection */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Preferred Countries
              </label>
              {mode === "view" ? (
                <div className="bg-gray-50 p-2 rounded-md border border-gray-200">
                  {(formData.countries_for_abroad_studies || []).length > 0
                    ? (formData.countries_for_abroad_studies || []).map(
                        (country, index) => (
                          <span
                            key={country}
                            className="inline-block bg-indigo-100 text-indigo-800 text-xs font-medium mr-2 mb-1 px-2.5 py-0.5 rounded-full"
                          >
                            {country}
                          </span>
                        )
                      )
                    : "N/A"}
                </div>
              ) : (
                <Select
                  isMulti
                  options={studyAbroadCountries}
                  value={studyAbroadCountries.filter((country) =>
                    (formData.countries_for_abroad_studies || []).includes(
                      country.value
                    )
                  )}
                  onChange={handleCountryChange}
                  className="w-full"
                  placeholder="Select countries"
                  styles={customSelectStyles}
                />
              )}
            </div>

            {/* Intake Information */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Preferred Intake
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mode === "view" ? (
                  <p className="bg-gray-50 p-2 rounded-md border border-gray-200 md:col-span-2">
                    {formData.intakeMonth && formData.intakeYear
                      ? `${formData.intakeMonth} ${formData.intakeYear}`
                      : "N/A"}
                  </p>
                ) : (
                  <>
                    <Select
                      options={availableMonths}
                      value={monthOptions.find(
                        (option) => option.value === formData.intakeMonth
                      )}
                      onChange={handleMonthChange}
                      isClearable
                      placeholder="Select month"
                      styles={customSelectStyles}
                    />

                    <Select
                      options={yearOptions}
                      value={yearOptions.find(
                        (option) => option.value === formData.intakeYear
                      )}
                      onChange={handleYearChange}
                      isClearable
                      placeholder="Select year"
                      styles={customSelectStyles}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div>
          <h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-800">
            4. Additional Information
          </h3>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Other Preferences or Information
            </label>
            {mode === "view" ? (
              <div className="bg-gray-50 p-3 rounded-md border border-gray-200 whitespace-pre-line">
                {formData.other_perfrences || "N/A"}
              </div>
            ) : (
              <textarea
                name="other_perfrences"
                rows={4}
                value={formData.other_perfrences || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter any additional preferences, requirements, or information that may be relevant..."
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreateLeadAbroadForm;
